import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Trophy, Clock, Target, TrendingUp, Sparkles, BookOpen, CalendarClock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/Skeleton";
import { InitialsAvatar } from "@/components/InitialsAvatar";
import { AvatarPortrait } from "@/components/AvatarPortrait";
import { Sparkline } from "@/components/Sparkline";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { cn } from "@/lib/utils";

const SKILL_TONE = {
    Novice: "border-amber-200 bg-amber-50 text-amber-800",
    Developing: "border-primary/30 bg-primary/10 text-primary",
    Proficient: "border-emerald-200 bg-emerald-50 text-emerald-700",
    Advanced: "border-violet-200 bg-violet-50 text-violet-700",
};

const formatRelative = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

export default function ProfilePage() {
    const { user } = useAuth();
    const [stats, setStats] = useState(null);
    const [learn, setLearn] = useState(null);
    const [error, setError] = useState(null);
    useDocumentTitle("Profile");

    useEffect(() => {
        let cancelled = false;
        Promise.all([api.get("/me/stats"), api.get("/learn/progress")])
            .then(([s, l]) => {
                if (!cancelled) {
                    setStats(s.data);
                    setLearn(l.data);
                }
            })
            .catch((e) => {
                if (cancelled) return;
                setError(e?.response?.data?.detail || "Could not load your stats");
                toast.error("Could not load your stats");
            });
        return () => { cancelled = true; };
    }, []);

    return (
        <Layout>
            <div className="space-y-8 animate-fade-in-up" data-testid="profile-page">
                {/* Identity */}
                <section className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                    <InitialsAvatar
                        name={user?.full_name || "?"}
                        className="h-16 w-16 text-xl"
                        dataTestId="profile-avatar"
                    />
                    <div>
                        <h1 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl" data-testid="profile-name">
                            {user?.full_name || "—"}
                        </h1>
                        <p className="mt-1 text-sm text-muted-foreground" data-testid="profile-email">
                            {user?.email}
                        </p>
                    </div>
                </section>

                {error && (
                    <Card className="border-destructive/30 bg-destructive/5">
                        <CardContent className="p-4 text-sm text-destructive">{String(error)}</CardContent>
                    </Card>
                )}

                {!stats && !error ? (
                    <ProfileSkeleton />
                ) : stats && (
                    <>
                        <SubscriptionSection user={user} />
                        <SkillLevelCard stats={stats} />
                        <StatsGrid stats={stats} />
                        <LearnProgressCard learn={learn} />
                        <Card className="border-border/80 shadow-sm" data-testid="profile-sparkline-card">
                            <CardContent className="space-y-3 p-6">
                                <div className="flex items-center gap-2">
                                    <TrendingUp className="h-4 w-4 text-primary" />
                                    <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                        Score trend — last {stats.recent_scores.length || "few"} sessions
                                    </h2>
                                </div>
                                <Sparkline values={stats.recent_scores} dataTestId="profile-sparkline" />
                                {stats.recent_scores.length > 0 && (
                                    <p className="text-xs text-muted-foreground">
                                        Dotted lines mark the &ldquo;average&rdquo; (50) and &ldquo;solid&rdquo; (70) thresholds.
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                        <ByTherapyCard stats={stats} />
                        <RecentActivityCard stats={stats} />
                    </>
                )}
            </div>
        </Layout>
    );
}

const ProfileSkeleton = () => (
    <div className="space-y-6" data-testid="profile-skeleton">
        <Skeleton className="h-32 w-full rounded-2xl" />
        <Skeleton className="h-24 w-full rounded-2xl" />
        <Skeleton className="h-40 w-full rounded-2xl" />
        <Skeleton className="h-40 w-full rounded-2xl" />
    </div>
);

const formatLongDate = (iso) => {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    } catch {
        return null;
    }
};

const SubscriptionSection = ({ user }) => {
    const sub = user?.subscription;
    if (!sub) return null;

    const status = sub.status;
    const plan = sub.plan;
    const days =
        typeof sub.days_remaining === "number"
            ? sub.days_remaining
            : typeof user.expires_in_days === "number"
                ? user.expires_in_days
                : null;
    const expiresOn = formatLongDate(sub.expires_at);

    const isComplimentary = status === "active" && plan === "complimentary";
    const isPaid = status === "active" && plan && plan !== "complimentary";
    const isFreeOrExpired = status === "free" || status === "expired";

    let planName = "Free";
    let statusLabel = "—";
    let statusTone = "border-border bg-secondary text-muted-foreground";
    if (isComplimentary) {
        planName = "Complimentary access";
        statusLabel = "Active";
        statusTone = "border-sky-200 bg-sky-50 text-sky-800";
    } else if (isPaid) {
        planName = plan.charAt(0).toUpperCase() + plan.slice(1).replace(/_/g, " ") + " subscription";
        statusLabel = "Active";
        statusTone = "border-amber-200 bg-amber-50 text-amber-800";
    } else if (status === "expired") {
        planName = "Subscription expired";
        statusLabel = "Expired";
        statusTone = "border-rose-200 bg-rose-50 text-rose-700";
    } else if (status === "free") {
        planName = "Free tier";
        statusLabel = "Free";
    }

    return (
        <Card className="border-border/80 shadow-sm" data-testid="profile-subscription-card">
            <CardContent className="space-y-4 p-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        <CalendarClock className="h-3.5 w-3.5 text-primary" />
                        Subscription
                    </div>
                    <span
                        className={cn(
                            "inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium",
                            statusTone
                        )}
                        data-testid="profile-subscription-status"
                    >
                        {statusLabel}
                    </span>
                </div>

                <div className="grid gap-4 sm:grid-cols-3">
                    <Field label="Plan" value={planName} testId="profile-subscription-plan" />
                    <Field
                        label="Expires on"
                        value={expiresOn || (isFreeOrExpired ? "—" : "Soon")}
                        testId="profile-subscription-expires-on"
                    />
                    <Field
                        label="Days remaining"
                        value={
                            typeof days === "number" ? (
                                <span className="font-semibold text-foreground tabular-nums" data-testid="profile-subscription-days-remaining">
                                    {days}
                                </span>
                            ) : (
                                <span className="text-muted-foreground" data-testid="profile-subscription-days-remaining">
                                    —
                                </span>
                            )
                        }
                        testId="profile-subscription-days-field"
                    />
                </div>

                {isComplimentary && (
                    <p className="text-sm text-muted-foreground" data-testid="profile-subscription-complimentary-note">
                        After your complimentary period, you&apos;ll need to subscribe to continue.
                    </p>
                )}
                {isPaid && (
                    <Button
                        asChild
                        variant="outline"
                        size="sm"
                        className="w-fit"
                        data-testid="profile-subscription-manage-link"
                    >
                        <Link to="/subscribe">Manage subscription</Link>
                    </Button>
                )}
                {isFreeOrExpired && (
                    <Button
                        asChild
                        size="sm"
                        className="w-fit"
                        data-testid="profile-subscription-upgrade-link"
                    >
                        <Link to="/subscribe">Subscribe</Link>
                    </Button>
                )}
            </CardContent>
        </Card>
    );
};

const Field = ({ label, value, testId }) => (
    <div className="space-y-1" data-testid={testId}>
        <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
        <div className="text-sm text-foreground">{value}</div>
    </div>
);

const SkillLevelCard = ({ stats }) => {
    const tone = SKILL_TONE[stats.skill_level] || SKILL_TONE.Novice;
    const pct = Math.round((stats.skill_level_progress ?? 0) * 100);
    return (
        <Card className="border-border/80 shadow-sm" data-testid="profile-skill-card">
            <CardContent className="p-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="space-y-2">
                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Skill level
                        </p>
                        <div className="flex items-center gap-3">
                            <span
                                className={cn(
                                    "inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm font-semibold",
                                    tone
                                )}
                                data-testid="profile-skill-badge"
                            >
                                <Trophy className="h-3.5 w-3.5" />
                                {stats.skill_level}
                            </span>
                            <span className="text-xs text-muted-foreground" data-testid="profile-skill-hours">
                                {stats.total_hours_practiced} h practised
                            </span>
                        </div>
                    </div>
                    {stats.skill_level_next && (
                        <div className="text-right">
                            <p className="text-xs uppercase tracking-wider text-muted-foreground">
                                Next: <span className="text-foreground font-medium">{stats.skill_level_next}</span>
                            </p>
                            <p className="text-xs text-muted-foreground" data-testid="profile-skill-next-hours">
                                {stats.skill_level_hours_to_next} h to go
                            </p>
                        </div>
                    )}
                </div>
                <div
                    className="mt-4 h-2 w-full overflow-hidden rounded-full bg-secondary"
                    role="progressbar"
                    aria-valuenow={pct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    data-testid="profile-skill-progress"
                >
                    <div
                        className="h-full rounded-full bg-primary transition-all duration-700"
                        style={{ width: `${pct}%` }}
                    />
                </div>
            </CardContent>
        </Card>
    );
};

const StatsGrid = ({ stats }) => (
    <div className="grid gap-4 sm:grid-cols-3" data-testid="profile-stats-grid">
        <StatTile
            icon={Sparkles}
            label="Sessions"
            value={stats.total_sessions}
            sub={`${stats.total_completed_sessions} finished`}
            testId="profile-stat-sessions"
        />
        <StatTile
            icon={Clock}
            label="Practice time"
            value={`${stats.total_hours_practiced} h`}
            sub={`${stats.total_minutes_practiced} min total`}
            testId="profile-stat-hours"
        />
        <StatTile
            icon={Target}
            label="Avg. score"
            value={stats.average_score != null ? Math.round(stats.average_score) : "—"}
            sub={
                stats.average_score != null
                    ? stats.average_score >= 70
                        ? "Solid"
                        : stats.average_score >= 50
                            ? "Average"
                            : "Below average"
                    : "Need a reviewed session"
            }
            testId="profile-stat-avg-score"
        />
    </div>
);

const StatTile = ({ icon: Icon, label, value, sub, testId }) => (
    <Card className="border-border/80 shadow-sm" data-testid={testId}>
        <CardContent className="space-y-2 p-5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
                <Icon className="h-3.5 w-3.5 text-primary" />
                {label}
            </div>
            <div className="text-3xl font-semibold tracking-tight text-foreground">{value}</div>
            <div className="text-xs text-muted-foreground">{sub}</div>
        </CardContent>
    </Card>
);

const LearnProgressCard = ({ learn }) => {
    const totalChapters = learn?.total_chapters ?? 0;
    return (
        <Card className="border-border/80 shadow-sm" data-testid="profile-learn-card">
            <CardContent className="space-y-3 p-5">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        <BookOpen className="h-3.5 w-3.5 text-primary" />
                        Learn progress
                    </div>
                    <Button asChild variant="ghost" size="sm" data-testid="profile-learn-link">
                        <Link to="/learn">
                            Open library
                            <ArrowRight className="h-3 w-3" />
                        </Link>
                    </Button>
                </div>
                {!learn ? (
                    <Skeleton className="h-6 w-72" />
                ) : (
                    <div className="space-y-3">
                        <p className="text-sm">
                            <span className="text-xl font-semibold text-foreground tabular-nums" data-testid="profile-learn-completed">
                                {learn.chapters_completed}
                            </span>{" "}
                            <span className="text-muted-foreground">
                                of {totalChapters} chapters complete
                            </span>
                            {learn.average_quiz_score != null && (
                                <span className="text-muted-foreground">
                                    {"  ·  Avg. quiz score "}
                                    <span
                                        className="text-foreground font-medium tabular-nums"
                                        data-testid="profile-learn-avg-score"
                                    >
                                        {Math.round(learn.average_quiz_score)}%
                                    </span>
                                </span>
                            )}
                        </p>
                        {/* Per-module mini-bars — one short stack per method */}
                        {Array.isArray(learn.by_module) && learn.by_module.length > 0 && (
                            <ul
                                className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3"
                                data-testid="profile-learn-by-module"
                            >
                                {learn.by_module.map((m) => {
                                    const pct = m.chapter_count
                                        ? Math.round((m.chapters_completed / m.chapter_count) * 100)
                                        : 0;
                                    return (
                                        <li
                                            key={m.module_slug}
                                            data-testid={`profile-learn-module-${m.module_slug}`}
                                            className="space-y-1"
                                        >
                                            <div className="flex items-baseline justify-between gap-2 text-xs">
                                                <Link
                                                    to={`/learn/${m.module_slug}`}
                                                    className="truncate font-medium text-foreground hover:underline"
                                                    data-testid={`profile-learn-module-${m.module_slug}-link`}
                                                >
                                                    {m.name}
                                                </Link>
                                                <span
                                                    className="shrink-0 tabular-nums text-muted-foreground"
                                                    data-testid={`profile-learn-module-${m.module_slug}-ratio`}
                                                >
                                                    {m.chapters_completed}/{m.chapter_count}
                                                </span>
                                            </div>
                                            <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                                                <div
                                                    className="h-full rounded-full bg-primary transition-all duration-700"
                                                    style={{ width: `${pct}%` }}
                                                    data-testid={`profile-learn-module-${m.module_slug}-bar`}
                                                />
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

const ByTherapyCard = ({ stats }) => {
    const rows = stats.by_therapy_type || [];
    if (rows.length === 0) {
        return (
            <Card className="border-border/80 shadow-sm" data-testid="profile-by-therapy-card">
                <CardContent className="p-6">
                    <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Practice by therapy type
                    </h2>
                    <p className="mt-3 text-sm text-muted-foreground">
                        Run a session to see which approaches you&apos;re leaning into most.
                    </p>
                </CardContent>
            </Card>
        );
    }
    const maxSessions = Math.max(...rows.map((r) => r.sessions), 1);
    return (
        <Card className="border-border/80 shadow-sm" data-testid="profile-by-therapy-card">
            <CardContent className="space-y-4 p-6">
                <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Practice by therapy type
                </h2>
                <ul className="space-y-3" data-testid="profile-by-therapy-list">
                    {rows.map((r) => {
                        const pct = (r.sessions / maxSessions) * 100;
                        return (
                            <li
                                key={r.therapy_type_slug}
                                className="grid grid-cols-[1fr_auto] gap-3"
                                data-testid={`profile-by-therapy-row-${r.therapy_type_slug}`}
                            >
                                <div className="space-y-1">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="font-medium text-foreground">{r.therapy_type_name}</span>
                                        <span className="tabular-nums text-muted-foreground">
                                            {r.sessions} session{r.sessions === 1 ? "" : "s"}
                                        </span>
                                    </div>
                                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                                        <div
                                            className="h-full rounded-full bg-primary"
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                </div>
                                <span
                                    className="self-center rounded-full border border-border bg-secondary px-2 py-0.5 text-xs font-medium tabular-nums text-muted-foreground"
                                    data-testid={`profile-by-therapy-avg-${r.therapy_type_slug}`}
                                    title="Average score for this therapy"
                                >
                                    avg {r.average_score != null ? Math.round(r.average_score) : "—"}
                                </span>
                            </li>
                        );
                    })}
                </ul>
            </CardContent>
        </Card>
    );
};

const RecentActivityCard = ({ stats }) => {    const rows = stats.recent_activity || [];
    return (
        <Card className="border-border/80 shadow-sm" data-testid="profile-recent-activity-card">
            <CardContent className="space-y-4 p-6">
                <div className="flex items-center justify-between">
                    <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                        Recent activity
                    </h2>
                    <Button asChild variant="ghost" size="sm" data-testid="profile-history-link">
                        <Link to="/history">
                            View all
                            <ArrowRight className="h-3 w-3" />
                        </Link>
                    </Button>
                </div>
                {rows.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                        You haven&apos;t run a session yet. Start your first one to see it here.
                    </p>
                ) : (
                    <ul className="divide-y divide-border" data-testid="profile-recent-activity-list">
                        {rows.map((row) => (
                            <li
                                key={row.id}
                                className="flex items-center gap-3 py-3"
                                data-testid={`profile-recent-activity-${row.id}`}
                            >
                                <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg">
                                    <AvatarPortrait
                                        name={row.avatar_name}
                                        imageUrl={row.avatar_image_url}
                                        aspect={row.is_couple ? "wide" : "square"}
                                        rounded="rounded-lg"
                                    />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-medium text-foreground">{row.avatar_name}</p>
                                    <p className="line-clamp-1 text-xs text-muted-foreground">{row.case_title}</p>
                                </div>
                                <div className="flex shrink-0 items-center gap-2">
                                    {row.overall_score != null && (
                                        <span className="rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary tabular-nums">
                                            {row.overall_score}
                                        </span>
                                    )}
                                    <span className="text-xs text-muted-foreground" title={row.ended_at || row.created_at}>
                                        {formatRelative(row.ended_at || row.created_at)}
                                    </span>
                                    {(row.status === "reviewed" || row.status === "completed") && (
                                        <Button asChild variant="ghost" size="sm" className="h-7 px-2 text-xs">
                                            <Link to={`/practice/review/${row.id}`} data-testid={`profile-recent-activity-${row.id}-link`}>
                                                View
                                            </Link>
                                        </Button>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </CardContent>
        </Card>
    );
};
